import { OFFLINE_MIN_PAUSE_TAGE } from '../../shared/pruefpunkte.js';
import { abschnitt, ersetze, h, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { ergebnisVon, melde } from '../ergebnisse.js';
import { startInfo } from '../geraet.js';
import { ANZAHL_ANLAGEN, ANZAHL_BILDER, legeTestbestandAn, loescheTestbestand, pruefeTestbestand, testbestand } from '../offline-daten.js';
import type { Ansicht } from '../router.js';
import { formatiereZeitpunkt, groesseText, vorWieLange } from '../zeit.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

/** Fragt dauerhaften Speicher an und meldet das Ergebnis. */
export async function fordereDauerhaftenSpeicher(): Promise<boolean | undefined> {
  if (!navigator.storage?.persist) {
    await melde('dauerhafter-speicher', 'nicht_unterstuetzt', 'Der Browser kennt navigator.storage.persist() nicht.');
    return undefined;
  }
  const vorher = await navigator.storage.persisted();
  const gewaehrt = vorher || (await navigator.storage.persist());
  await melde(
    'dauerhafter-speicher',
    gewaehrt ? 'ok' : 'fehler',
    gewaehrt ? 'Das Gerät gewährt dauerhaften Speicher.' : 'Das Gerät gewährt keinen dauerhaften Speicher (persist() lieferte false).',
    { vorher, gewaehrt },
  );
  return gewaehrt;
}

/** Beim Start: Ist der Speicher schon dauerhaft, das einmal festhalten. */
export async function pruefeDauerhaftenSpeicherStill(): Promise<void> {
  if (!navigator.storage?.persisted) return;
  if (await navigator.storage.persisted()) {
    if (ergebnisVon('dauerhafter-speicher')?.status !== 'ok') {
      await melde('dauerhafter-speicher', 'ok', 'Das Gerät gewährt dauerhaften Speicher.', { vorher: true, gewaehrt: true });
    }
  }
}

export const speicher: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const statusSpeicher = meldung();
  const statusBestand = meldung();
  const belegung = h('div', {});
  const bestandInfo = h('div', {});

  async function zeigeBelegung(): Promise<void> {
    if (!navigator.storage?.estimate) {
      ersetze(belegung, h('p', { class: 'leise' }, 'Der Browser verrät die Belegung nicht.'));
      return;
    }
    const schaetzung = await navigator.storage.estimate();
    const dauerhaft = navigator.storage.persisted ? await navigator.storage.persisted() : false;
    ersetze(
      belegung,
      liste(
        zeile('Dauerhafter Speicher', dauerhaft ? 'ja' : 'nein'),
        zeile('Belegt', groesseText(schaetzung.usage ?? 0)),
        zeile('Höchstens möglich', schaetzung.quota ? groesseText(schaetzung.quota) : 'unbekannt'),
      ),
    );
  }

  async function zeigeBestand(): Promise<void> {
    const meta = await testbestand();
    ersetze(
      bestandInfo,
      meta
        ? liste(
            zeile('Angelegt', `${formatiereZeitpunkt(meta.angelegtAm)} (${vorWieLange(meta.angelegtAm)})`),
            zeile('Umfang', `${meta.anlagen} Anlagen, ${meta.bilder} Vorschaubilder, ${groesseText(meta.bytes)}`),
            zeile('Letzte Nutzung vor diesem Start', startInfo().zuletztAktivVorher ? vorWieLange(startInfo().zuletztAktivVorher ?? '') : 'unbekannt'),
          )
        : h('p', {}, 'Noch kein Testbestand auf diesem Gerät.'),
      meta
        ? h(
            'div',
            { class: 'knopfreihe' },
            knopf('Jetzt prüfen', async () => {
              const ergebnis = await pruefeTestbestand(startInfo(), true);
              if (ergebnis) setzeMeldung(statusBestand, ergebnis.text, ergebnis.vollstaendig ? 'ok' : 'fehler');
            }),
            knopf(
              'Testbestand löschen',
              async () => {
                if (!window.confirm('Testbestand wirklich löschen? Der Test „Offline-Daten nach Tagen“ beginnt dann von vorn.')) return;
                await loescheTestbestand();
                setzeMeldung(statusBestand, 'Testbestand gelöscht.', 'info');
                await zeigeBestand();
              },
              'gefahr',
            ),
          )
        : knopf(`Testbestand anlegen (${new Intl.NumberFormat('de-AT').format(ANZAHL_ANLAGEN)} Anlagen, ${ANZAHL_BILDER} Bilder)`, async () => {
            await legeTestbestandAn((text) => setzeMeldung(statusBestand, text));
            setzeMeldung(statusBestand, 'Testbestand angelegt.', 'ok');
            await zeigeBestand();
            await zeigeBelegung();
          }),
    );
  }

  ziel.append(
    ...kopf('Speicher und Offline', 'Trägt das ganze Offline-Konzept: Gewährt das Gerät dauerhaften Speicher, und sind die Offline-Daten nach einigen Tagen ohne Nutzung noch da?'),
    abschnitt(
      'Dauerhafter Speicher',
      pruefpunktStand('dauerhafter-speicher', aufraeumer),
      h('p', {}, 'Am Android gewährt Chrome ihn eher, wenn die App installiert ist und Push erlaubt wurde. Dann erneut anfordern.'),
      knopf('Dauerhaften Speicher anfordern', async () => {
        const gewaehrt = await fordereDauerhaftenSpeicher();
        setzeMeldung(statusSpeicher, gewaehrt === undefined ? 'Nicht unterstützt.' : gewaehrt ? 'Gewährt.' : 'Nicht gewährt.', gewaehrt ? 'ok' : 'fehler');
        await zeigeBelegung();
      }),
      statusSpeicher,
      belegung,
    ),
    abschnitt(
      'Offline-Daten nach Tagen',
      pruefpunktStand('offline-daten', aufraeumer),
      schritte(
        'Testbestand anlegen.',
        `Die App mindestens ${OFFLINE_MIN_PAUSE_TAGE} Tage nicht öffnen (am besten auch nach 8 bis 10 Tagen noch einmal prüfen).`,
        'App öffnen: Beim Start prüft sie selbst, ob noch alles da ist.',
      ),
      bestandInfo,
      statusBestand,
    ),
    abschnitt(
      'Start ohne Netz',
      pruefpunktStand('offline-start', aufraeumer),
      schritte('Flugmodus einschalten.', 'App ganz schließen und neu öffnen.', 'Die App muss starten und „ohne Netz gestartet“ melden. Das Ergebnis geht hinaus, sobald wieder Netz da ist.'),
    ),
  );
  await zeigeBelegung();
  await zeigeBestand();
  return aufraeumer.alles;
};
